import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Check, X, Download, FileText } from 'lucide-react';
import toast from 'react-hot-toast';

const QuoteAcceptanceForm = ({ 
  viewHash, 
  optionalServicesSelected, 
  onAcceptanceComplete,
  companyColors,
  onDownloadPdf
}) => {
  const [formData, setFormData] = useState({
    signerName: '',
    signerCompany: '',
    signerLocation: '',
    // Datum ist heute und wird beim Submit gesetzt
    customerNotes: ''
  });
  const [wantsSignature, setWantsSignature] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const canvasRef = useRef(null);
  const [signatureExists, setSignatureExists] = useState(false);

  // Today's date formatted as German string (read-only)
  const todayStr = new Date().toLocaleDateString('de-DE');

  const startDrawing = (e) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
    setSignatureExists(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  };

  const stopDrawing = () => setIsDrawing(false);

  const clearSignature = () => {
    const canvas = canvasRef.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    setSignatureExists(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.signerName.trim()) { toast.error('Bitte geben Sie Ihren Namen ein'); return; }
    if (!formData.signerLocation.trim()) { toast.error('Bitte geben Sie den Ort ein'); return; }
    if (wantsSignature && !signatureExists) { toast.error('Bitte unterschreiben Sie das Angebot'); return; }

    setSubmitting(true);
    try {
      let signatureData = null;
      if (wantsSignature && canvasRef.current) {
        signatureData = canvasRef.current.toDataURL('image/png');
      }

      const acceptanceData = {
        signerName: formData.signerName,
        signerCompany: formData.signerCompany,
        signerLocation: formData.signerLocation,
        signatureDate: todayStr,
        customerNotes: formData.customerNotes,
        digitalSignature: signatureData,
        optionalSelections: optionalServicesSelected,
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quotes/view/${viewHash}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(acceptanceData),
      });

      if (!response.ok) throw new Error('Fehler beim Speichern der Annahme');

      const result = await response.json();
      toast.success('Angebot erfolgreich angenommen!');
      // Kurze Verzögerung damit die eigene Bestätigung kurz sichtbar ist
      setAccepted(true);
      setTimeout(() => {
        if (onAcceptanceComplete) onAcceptanceComplete(result);
      }, 1500);

    } catch (error) {
      toast.error('Fehler beim Annehmen des Angebots');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── After acceptance: show confirmation + PDF download ───
  if (accepted) {
    return (
      <Card className="print-card bg-green-50 border-green-200">
        <CardContent className="pt-8 pb-8">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <Check className="w-9 h-9 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-green-800">Angebot angenommen!</h3>
            <p className="text-slate-600">
              Vielen Dank, {formData.signerName}. Ihr Angebot wurde am {todayStr} verbindlich angenommen.
            </p>
            {onDownloadPdf && (
              <Button
                onClick={onDownloadPdf}
                className="mt-4"
                style={{ backgroundColor: companyColors?.primary }}
                data-testid="pdf-download-after-acceptance"
              >
                <Download className="w-4 h-4 mr-2" />
                Angebot als PDF herunterladen
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="print-card" style={{ borderTop: `4px solid ${companyColors?.primary}` }}>
      <CardHeader>
        <CardTitle className="text-xl">Angebot annehmen</CardTitle>
        <p className="text-sm text-slate-600 mt-1">
          Füllen Sie die Felder aus und nehmen Sie das Angebot verbindlich an.
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-5">
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="signerName">Name *</Label>
              <Input id="signerName" value={formData.signerName}
                onChange={(e) => setFormData({...formData, signerName: e.target.value})}
                placeholder="Max Mustermann" />
            </div>
            <div>
              <Label htmlFor="signerCompany">Firma (optional)</Label>
              <Input id="signerCompany" value={formData.signerCompany}
                onChange={(e) => setFormData({...formData, signerCompany: e.target.value})}
                placeholder="Mustermann GmbH" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="signerLocation">Ort *</Label>
              <Input id="signerLocation" value={formData.signerLocation}
                onChange={(e) => setFormData({...formData, signerLocation: e.target.value})}
                placeholder="Berlin" />
            </div>
            <div>
              <Label htmlFor="signatureDate">Datum</Label>
              <Input id="signatureDate" value={todayStr} readOnly
                className="bg-slate-50 text-slate-500 cursor-not-allowed" />
            </div>
          </div>

          <div>
            <Label htmlFor="customerNotes">Anmerkungen (optional)</Label>
            <Textarea id="customerNotes" value={formData.customerNotes}
              onChange={(e) => setFormData({...formData, customerNotes: e.target.value})}
              placeholder="Ihre Anmerkungen zum Angebot..." rows={2} />
          </div>

          {/* Optional Signature */}
          <div className="border rounded-lg p-4 bg-slate-50">
            <label className="flex items-center gap-3 cursor-pointer mb-3">
              <input
                type="checkbox"
                checked={wantsSignature}
                onChange={(e) => setWantsSignature(e.target.checked)}
                className="w-4 h-4 rounded"
              />
              <span className="text-sm font-medium text-slate-700">
                Ich möchte mit digitaler Unterschrift bestätigen (optional)
              </span>
            </label>

            {wantsSignature && (
              <div>
                <canvas
                  ref={canvasRef}
                  width={600}
                  height={160}
                  className="border-2 border-slate-300 rounded-lg cursor-crosshair w-full"
                  style={{ touchAction: 'none', backgroundColor: '#fff' }}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    const touch = e.touches[0];
                    const canvas = canvasRef.current;
                    const rect = canvas.getBoundingClientRect();
                    const ctx = canvas.getContext('2d');
                    setIsDrawing(true);
                    ctx.beginPath();
                    ctx.moveTo(touch.clientX - rect.left, touch.clientY - rect.top);
                    setSignatureExists(true);
                  }}
                  onTouchMove={(e) => {
                    e.preventDefault();
                    if (!isDrawing) return;
                    const touch = e.touches[0];
                    const canvas = canvasRef.current;
                    const rect = canvas.getBoundingClientRect();
                    const ctx = canvas.getContext('2d');
                    ctx.lineTo(touch.clientX - rect.left, touch.clientY - rect.top);
                    ctx.strokeStyle = '#000';
                    ctx.lineWidth = 2;
                    ctx.lineCap = 'round';
                    ctx.stroke();
                  }}
                  onTouchEnd={() => setIsDrawing(false)}
                />
                <div className="flex items-center gap-2 mt-2">
                  <p className="text-xs text-slate-500 flex-1">Mit Maus oder Finger hier unterschreiben</p>
                  <Button type="button" variant="outline" size="sm" onClick={clearSignature}>
                    <X className="w-3 h-3 mr-1" /> Löschen
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="p-3 bg-slate-50 rounded-lg text-xs text-slate-600">
            Datum, Uhrzeit und IP-Adresse werden bei der Annahme dokumentiert und rechtssicher gespeichert.
          </div>

          <Button type="submit" disabled={submitting} className="w-full text-base py-3"
            style={{ backgroundColor: companyColors?.primary }}
            data-testid="accept-quote-btn">
            {submitting ? 'Wird gespeichert...' : (
              <><Check className="w-4 h-4 mr-2" />Angebot verbindlich annehmen</>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default QuoteAcceptanceForm;
